<template>
  <div
    class="customer-page d-flex flex-column"
    :class="{ 'has-tabs': creditInsightsTabsEnabled }"
  >
    <header
      id="customer-header"
      class="customer-header d-flex justify-content-between align-items-center"
    >
      <div class="left-side d-flex flex-row">
        <!-- if the user is coming from analytics, we need to preserve the query params -->
        <router-link
          :to="{
            name: 'customers-view',
            ...(isFromAnalytics() ? { query: $route.query } : {}),
          }"
          class="back-link"
        >
          <i-icon-button
            class="back-button"
            data-testid="customer-page-back-button"
            symbol="medium-arrow-left"
            type="transparent"
          ></i-icon-button>
        </router-link>
        <div class="customer-icon i-ml-6 i-mr-6">
          <i-icon
            v-if="selectedCustomer"
            class="i-text-primary-110"
            symbol="small-user-single"
            :size="16"
          ></i-icon>
        </div>
        <div v-if="selectedCustomer">
          <div class="i-text-button-2 i-mb-2 customer-title">
            <div class="d-flex flex-row">
              <i-inline-input
                :value="selectedCustomer.name"
                data-testid="customer-name-input"
                class="customer-name-input i-mr-2"
                placeholder="Customer name"
                @input="selectedCustomer.name = $event.target.value"
                @debounced-input="updateCustomerName"
              />
              <customer-fraud-rating
                v-if="selectedCustomer.fraud_rating"
                :fraud-rating="selectedCustomer.fraud_rating"
              ></customer-fraud-rating>
            </div>
          </div>

          <div
            class="customer-subtitle i-mt-1 i-text-subheading-3 d-flex i-text-neutral-110"
          >
            <div class="d-flex flow-row align-items-center">
              <i-icon
                symbol="small-calendar-date"
                :size="12"
                class="i-text-neutral-90"
              ></i-icon>
              <span class="i-ml-4 i-mr-8"
                ><span class="created-label i-mr-2">Created</span
                >{{ formatDateTime(selectedCustomer.created_at, 'lll') }}</span
              >
            </div>
            <div class="d-flex flow-row align-items-center">
              <i-icon
                symbol="small-user-edit"
                :size="12"
                class="i-text-neutral-90"
              ></i-icon>
              <span class="i-ml-4">{{ customerCreatorEmail }}</span>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="selectedCustomer"
        data-testid="customer-header-actions"
        class="right-side d-flex align-items-center justify-content-end"
      >
        <i-icon-button
          id="customer-blocklist-trigger"
          :disabled="
            (groupedParsedDetails && groupedParsedDetails.length === 0) ||
            showBlocklistSuggestions
          "
          class="blocklist-button"
          symbol="medium-user-block"
          type="transparent"
          tooltip="Add to Blocklist"
          tooltip-container="customer-header"
          @button-click="() => onClickShowBlocklist()"
        ></i-icon-button>
        <i-icon-button
          data-test="customer-history"
          symbol="medium-clock-rewind"
          type="transparent"
          size="md"
          tooltip="Status History"
          tooltip-container="customer-header"
          @button-click="historyOpen = true"
        ></i-icon-button>
        <i-icon-button
          v-if="isPdfExportEnabled"
          :disabled="!isCompletePdfExport"
          class="export-button"
          symbol="medium-download"
          type="transparent"
          tooltip="Export"
          tooltip-container="customer-header"
          @button-click="$emit('get-pdf-report')"
        ></i-icon-button>
        <i-icon-button
          class="delete-customer-button i-mr-6"
          symbol="medium-delete-bin-1"
          type="transparent"
          tooltip="Delete"
          tooltip-container="customer-header"
          @button-click="$emit('delete-customer')"
        ></i-icon-button>
        <customer-approval-buttons
          :customer="selectedCustomer"
          :grouped-parsed-details="groupedParsedDetails"
          :show="showBlocklistSuggestions"
        />
        <customer-blocklist-suggestions
          v-if="selectedCustomer.parsedDetails"
          :customer="selectedCustomer"
          :grouped-parsed-details="groupedParsedDetails"
          :show="showBlocklistSuggestions"
          @input-text="
            (index, type, value) => (groupedParsedDetails[index][type] = value)
          "
        />
      </div>
    </header>
    <customer-page-tabs v-if="creditInsightsTabsEnabled"></customer-page-tabs>
    <CustomerStatusHistory
      v-if="historyOpen"
      class="document-status-history"
      :show="historyOpen"
      :customer-created-at="selectedCustomer.created_at"
      @hide-panel="historyOpen = false"
    />
    <router-view></router-view>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations, mapState } from 'vuex';
import CustomerBlocklistSuggestions from '@/V2/customers/CustomerBlocklistSuggestions';
import CustomerPageTabs from '@/V2/customers/CustomerPageTabs';
import * as parsedDetailsTypes from '@/constants/parsedDetailsTypes';
import parsedDetailsConfig from '@/constants/parsedDetailsConfig';
import CustomerApprovalButtons from '@/V2/customers/CustomerApprovalButtons';
import { iIcon, iIconButton, iInlineInput } from '@inscribe/signal';
import { showNotification } from '@/utils/notifications';
import CustomerFraudRating from '@/V2/customers/CustomerFraudRating.vue';
import CustomerStatusHistory from '@/V2/customers/CustomerStatusHistory';
import { formatDateTime } from '@/utils/date';

export default {
  components: {
    'customer-approval-buttons': CustomerApprovalButtons,
    'customer-blocklist-suggestions': CustomerBlocklistSuggestions,
    'customer-page-tabs': CustomerPageTabs,
    iIcon,
    iIconButton,
    iInlineInput,
    CustomerFraudRating,
    CustomerStatusHistory,
  },
  props: {
    isPdfExportEnabled: {
      type: Boolean,
      default: false,
    },
    isCompletePdfExport: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      historyOpen: false,
    };
  },
  computed: {
    ...mapGetters({
      creditInsightsTabsEnabled: 'creditInsightsTabsEnabled',
    }),
    ...mapState({
      selectedCustomer: (state) => state.customers.selectedCustomer,
      groupedParsedDetails: (state) => state.customers.groupedParsedDetails,
      showBlocklistSuggestions: (state) =>
        state.customers.showBlocklistSuggestions,
    }),
    customerCreatorEmail() {
      return this.selectedCustomer?.creator?.email || '';
    },
  },
  watch: {
    selectedCustomer: {
      immediate: true,
      handler: function (newValue, oldValue) {
        if (newValue && newValue !== oldValue) {
          this.getCustomerParsedDetails(newValue.id)
            .then(({ data }) => {
              newValue.parsedDetails = data.data;
              const parsedDetails = this.getGroupedParsedDetails(data.data);
              this.setGroupedParsedDetails(parsedDetails);
            })
            .catch((err) => {
              showNotification({
                type: 'error',
                title: 'Error fetching customer parsed details',
                text: err.response?.data?.message,
              });
            });
        }
      },
    },
  },
  methods: {
    ...mapActions(['getCustomerParsedDetails']),
    ...mapMutations(['setShowBlocklistSuggestions', 'setGroupedParsedDetails']),
    isFromAnalytics() {
      return this.$route.query.return_to === 'analytics';
    },
    formatDateTime,
    getGroupedParsedDetails(parsedDetails) {
      const { show: showAddressesFn } =
        parsedDetailsConfig[parsedDetailsTypes.ADDRESS];

      const addresses = parsedDetails.filter(
        ({ instances, type }) =>
          type === parsedDetailsTypes.ADDRESS &&
          instances.some(showAddressesFn),
      );

      const { show: showNameFn } = parsedDetailsConfig[parsedDetailsTypes.NAME];
      return parsedDetails
        .filter(
          ({ instances, type }) =>
            type === parsedDetailsTypes.NAME && instances.some(showNameFn),
        )
        .reduce((acc, parsedName) => {
          const documentIds = parsedName.instances.map(
            ({ document_id }) => document_id,
          );
          const matchingAddress = addresses.find(({ instances }) =>
            instances.find((addressInstance) =>
              documentIds.includes(addressInstance.document_id),
            ),
          );
          if (matchingAddress) {
            return [
              ...acc,
              {
                name: parsedName.normalized,
                address: matchingAddress.normalized,
              },
            ];
          }
          return acc;
        }, []);
    },
    updateCustomerName() {
      if (this.selectedCustomer.name.length > 0) {
        this.$store.dispatch('updateCustomer', this.selectedCustomer).then(
          (response) => {
            this.$store.commit('updateCustomerState', {
              objectsName: 'list',
              updatedObject: response.data,
            });
          },
          ({ response: { data, status } }) => {
            this.$notify({
              group: 'general',
              type: 'error',
              title: 'Error changing customer name',
              duration: 5000,
              text:
                status === 403
                  ? 'You do not have permission to change the customer name'
                  : data.message,
            });
          },
        );
      }
    },
    onClickShowBlocklist() {
      this.setShowBlocklistSuggestions(true);
    },
  },
};
</script>

<style lang="scss">
/* global styles */
.redesign-customer-view-body {
  overflow: hidden !important;
}
</style>

<style lang="scss" scoped>
.customer-page {
  height: 100vh;
  overflow: hidden;

  &.has-tabs {
    header {
      box-shadow: none;
    }
  }

  header {
    height: 64px;
    padding: 16px 24px;
    background-color: $light-neutral-default;
    box-shadow: $box-shadow-default;
    z-index: 50;

    .left-side {
      align-items: center;

      .customer-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        background: $light-primary-30;
        border-radius: 200px;
      }

      .customer-title {
        line-height: 18px;
      }

      .customer-subtitle {
        align-items: center;
        color: $light-neutral-110;
        flex-direction: row;

        @include respond-to('medium') {
          flex-direction: column;
          align-items: flex-start;

          .created-label {
            display: none;
          }
        }
      }
    }
  }
}
</style>
